import { Button, Image, Progress, UploadProps } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { RiCheckLine, RiCloseLine, RiEyeFill } from 'react-icons/ri';
import { IconCsvFileIcon, IconFolder } from '@/assets/static/icons';
import { AppModal } from '@components/form/Modal';
import { AppPrimaryButton } from '@components/form/button/app-primary-button';
import { formatFileSize } from '@shared/utils';
import {
  AppUploadDocumentFileList,
  AppUploadFileList,
  AppUploadTypes,
} from '@shared/types/components/antd/app-upload.types';
import { truncateMiddle } from '../../../../modules/settings/subscription/utils/utils';
import { UploadFile } from 'antd/lib';
import { Trans, useTranslation } from 'react-i18next';
import { getFileTypeIcon, isImageFile } from '@shared/utils/file.helper';
import { capitalize } from '@shared/utils/capitalize';

export function AppUpload({ title, templateName, importModal, downloadUrl, importContact }: AppUploadTypes) {
  const { t } = useTranslation();
  const { handleRemoveClick, uploadProps, handleConfirmUpload, fileList, loading, closeImportModal } = importContact;
  const hasFile = !!fileList?.length;

  return (
    <>
      <AppModal
        title={
          <AppModal.Header>
            <AppModal.Title className="mb-2">{title}</AppModal.Title>
            <AppModal.SubTitle>
              <Trans
                i18nKey="app_upload_modal_description"
                components={[
                  <br key="line-break" />,
                  <a key="download-template" href={downloadUrl} download={templateName} className="underline!" />,
                ]}
              />
            </AppModal.SubTitle>
          </AppModal.Header>
        }
        open={importModal.open}
        onCancel={closeImportModal}
        destroyOnHidden
        footer={
          // hasFile ? (
          <div className="space-y-2 gap-4 flex">
            <Button className="w-full!" onClick={closeImportModal}>
              {capitalize(t('cancel'))}
            </Button>
            <AppPrimaryButton
              block
              onClick={handleConfirmUpload}
              disabled={!fileList?.length || loading}
              loading={loading}
            >
              {title}
            </AppPrimaryButton>
          </div>
          // ) : null
        }
      >
        <AppModal.Body className="mt-5">
          <AppUpload.Dragger {...uploadProps} progress={importContact?.uploadProgress} />
          {hasFile && <AppUpload.FileList fileList={fileList} handleRemoveClick={handleRemoveClick} />}
        </AppModal.Body>
      </AppModal>
    </>
  );
}

AppUpload.Dragger = ({ progress, ...uploadProps }: { progress: number }) => {
  return (
    <Dragger {...uploadProps}>
      <AppUpload.DraggerBody progress={progress} />
    </Dragger>
  );
};

AppUpload.DraggerBody = ({ progress }: { progress: number }) => (
  <>
    <div className="flex justify-center p-2">
      <div className="bg-surface p-4 rounded-2xl">
        <IconFolder />
      </div>
    </div>

    {progress != 0 && (
      <div className="mt-2">
        <div className="flex justify-center items-center flex-col">
          <Progress size={40} type="circle" percent={progress} />
          {progress !== 100 && (
            <p>
              <Trans i18nKey="uploading_contacts" />
            </p>
          )}
          {progress === 100 && (
            <p>
              <Trans i18nKey="upload_completed" />
            </p>
          )}
        </div>
      </div>
    )}

    <>
      <p className="ant-upload-text">
        <Trans
          i18nKey="drop_your_file_here_or_browse"
          components={[<span key="browse-file" className="underline! text-primary!" />]}
        />
      </p>
      <p className="ant-upload-hint">
        <Trans i18nKey="upload_max_file_size" values={{ size: 5 }} />
      </p>
    </>
  </>
);

AppUpload.FileList = ({ fileList, handleRemoveClick }: AppUploadFileList) => {
  return (
    <div className="mt-4">
      {fileList?.map((file) => {
        return (
          <div
            key={file.uid}
            className="group bg-surface border border-gray-200 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              <IconCsvFileIcon className="text-white" />
              <div className="flex flex-col">
                <span className=" text-text-700 ">{file.name}</span>
                <span className="text-sm text-text-400">{formatFileSize(file.size || 0)}</span>
              </div>
            </div>

            <div className="cursor-pointer transition-all duration-500" onClick={() => handleRemoveClick(file)}>
              <div className="block group-hover:hidden p-2 border border-gray-400 rounded-full">
                <RiCheckLine className="text-emerald-600" />
              </div>
              <div className="hidden group-hover:flex">
                <Button shape="circle" icon={<RiCloseLine className="text-text-500 mt-1!" />} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

AppUpload.DraggerDocument = (props: UploadProps) => (
  <Dragger {...props} className="[&>.ant-upload-drag]:bg-primary-25!" />
);

type DraggerDocumentBodyProps = {
  title?: React.ReactElement;
  subtitle?: React.ReactElement;
};

const DraggerDocumentBody = ({ title, subtitle }: DraggerDocumentBodyProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center gap-4">
      <div className="flex justify-center">
        <div className="bg-surface p-2 rounded-2xl" style={{ boxShadow: ' 3.06px 9.19px 34.04px -4.43px #1634501A' }}>
          <IconFolder width={40} height={40} />
        </div>
      </div>
      <div className="text-left">
        {title ?? (
          <>
            <p className="ant-upload-text text-[15px]!">
              <Trans
                i18nKey="drop_your_file_here_or_browse"
                components={[<span key="browse-file" className="underline! text-primary!" />]}
              />
            </p>
          </>
        )}
        {subtitle ?? <p className="ant-upload-hint text-xs!">{t('jpg_png_or_pdf_with_5_mb_max')}</p>}
      </div>
    </div>
  );
};
AppUpload.DraggerDocumentBody = DraggerDocumentBody;

AppUpload.DocumentFileList = ({ fileList, handleRemoveClick }: AppUploadDocumentFileList) => {
  return (
    <div
      className="mt-4 space-y-2"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {fileList?.map((file) => {
        const isImage = isImageFile(file);
        const fileType = !isImage ? getFileTypeIcon(file) : null;
        return (
          <div
            key={file.uid}
            className="group bg-surface border border-gray-200 rounded-lg p-3 flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              {isImage ? (
                <Image
                  alt="img"
                  width={70}
                  height={40}
                  src={file?.preview?.data?.url}
                  className="object-cover rounded border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                  preview={{ mask: <RiEyeFill /> }}
                />
              ) : (
                <div
                  className={`w-[70px] h-[40px] rounded border border-gray-200 flex flex-col items-center justify-center gap-0.5 ${fileType?.bg}`}
                >
                  <span className={`text-lg leading-none ${fileType?.text}`}>{fileType?.icon}</span>
                  <span className={`text-[9px] font-semibold tracking-wide ${fileType?.text}`}>{fileType?.label}</span>
                </div>
              )}
              <div className="flex flex-col">
                <span className=" text-text-700 " title={file.name}>
                  {truncateMiddle(file.name, 25)}
                </span>
                <span className="text-sm text-text-400 text-left">{formatFileSize(file.size || 0)}</span>
              </div>
            </div>

            <div
              className="cursor-pointer transition-all duration-500"
              onClick={() => handleRemoveClick(file as UploadFile)}
            >
              <div className="block group-hover:hidden p-2 border border-gray-400 rounded-full">
                <RiCheckLine className="text-emerald-600" />
              </div>
              <div className="hidden group-hover:flex">
                <Button
                  shape="circle"
                  className="min-w-[34px]! h-[34px]! w-[34px]!"
                  icon={<RiCloseLine className="text-text-500 mt-1!" />}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
